// 批次 43（#41 GNL-005-e，issue #41）：共同編輯表單查詢（form-co-edit-form-query-page）整頁 body。
//
// origin：WEB-INF/views/form/coEditForm/queryPage.jsp（246 行）＋js-my/coEditForm/queryPage.js（385 行）
// ＋CoEditFormUtils.js（184 行）。complexBody hook（批次 16 先例；ComplexFormPage 同款早退）。
//
// 【為什麼整頁自訂】泛用 LegacyContractFields 一欄一 label（generated 的 label 為「欄位級」，
//   來自 raw DOM 鄰近配置）：formDepartment/formTemplate/account 三欄共用 legacy 的同一個
//   「表單名稱：／參與表單填寫員：」label、classDateEnd 的 label 在 legacy 屬「* 課程日期：」群、
//   formDone radio×3 被 name 去重成 1 顆（批次 41 同根因）、onlyTodoForm 無 name 抽不到——
//   泛用渲染無法表達＝complexBody 裁決（批次 41 ClassFormListBody 同構前例；裁決 2026-10-02）。
//
// 【鏡像範圍（queryPage.jsp 行號，node 逐檔掃描親驗 2026-10-02）】
//   - 查詢 form（:36-209，Enter 送出＝原生 submit）：
//     學年度/醫院/訓練類別/訓練計畫（契約 1:1 欄位照畫）→ 表單名稱：label＋formDepartment＋
//     formTemplate(multiple)（:102-113）→ 參與表單填寫員：label＋keyword＋account（:115-125）→
//     * 課程日期：label＋classDateStart/End（readonly required maxlength=10）＋reset 圓鈕
//     （:127-154，icon-only 空文字按鈕＝批次 41 前例）→ 啟用狀態：enableType（:155-162）→
//     表單進行狀態：formDone radio×3（:165-176 全部''checked/進行中 false/已完成 true）→
//     自己待填的表單：onlyTodoForm checkbox＋(只顯示自己待填的表單)（:179-183）。
//   - 按鈕列（:186-207）：查詢(submit)/啟用/停用/刪除/變更為未完成（shiro 權限 gating＝admin
//     全顯；雛形以全顯鏡像【雛形值待實機校準】）。
//   - 右欄說明（:211-218）：說明:＋p＋ol×3＝LegacyAsideBlocks（legacy-aside-notes 地圖）。
//   - 表格（:221-232 formListTable）：表頭 11 欄（legacy-table-headers，第一欄空＝select-all
//     :227 input#select-all）；列 checkbox＝formCheckBox（queryPage.js:122-123），選取跨頁累積
//     （getSelectedForms :377-384 page:'all' 同語意）。
//   - 批量（queryPage.js:110-148＋CoEditFormUtils.js:103-143）：刪除＝sysConfirm「請問是否要
//     刪除?」→ remove → info(desc)＋重查（有 remove: endpoint key＝帶 dataEffect）；啟用/停用/
//     變更為未完成＝無 confirm 直打（legacy 無空選驗證）→ update({enabled}/{done})＋info＋重查
//     ——**endpoint（/coEditForm/enable・disable・reOpen）未映射＝不帶 dataEffect**（D21-4 同款
//     誠實登記；接線率不計，端點映射留待真後端批次 #19）。
//   - 查詢送出（queryPage.js:156-263）：formDone=''不送（:287）、onlyTodoForm=true 才送（:269-271）
//     、onlyTodoForm=true 跳過 form valid()（:160，日期 required 的唯一例外）——雛形 filters 直送
//     MockDataSource，過濾語意【雛形值待實機校準】（批次 23 同款揭露）。
//
// 【誠實標註】表格列＝種子（legacy raw-dom 表為空列）＝GNL-006 資料落差 AD；nav 分頁列/匯出/
//   關鍵字殼＝DataTableShell chrome＝GNL-003 同族 AD；enable/disable/reOpen 端點未映射（D21-4）。
//
// 【證據等級】§7 第 3 級（原始碼，行號親讀 2026-10-02）。

import { useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Table, type TableColumn } from '@/components/base/Table';
import { ContentPanel, useDialogs } from '@/components/layouts';
import { DataTableShell } from '@/components/layouts/DataTableShell';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import { LegacyAsideBlocks } from '@/mocks/legacy-aside-notes';
import {
  columnsFor,
  DEFAULT_PAGE_SIZE,
  initialValues,
  legacyContractFieldsFor,
  legacyTableSequenceFor,
  type LegacyFieldControl,
  type Row,
  resolveLegacyMessage,
  toPayload,
  useDataSource,
  useList,
} from '@/mocks';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/app/routes.generated';
import { LEGACY_FIELDS } from '@/mocks/legacy-fields.generated';
import { LegacyContractFields, checkedValueOf } from '../../templates/LegacyContractFields';
import { legacyAlertFallback } from '../../templates/page-behaviors';
import { rowUiKey, TemplateError, toUiKey } from '../../templates/shared';

export function CoEditQueryPageBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 46（FRM-002，Gitea issue #44）：名稱連結 → 共同編輯填寫頁（站內導航鏡像）。
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // 查詢區（queryPage.jsp:36-209）。1:1 契約欄位照畫；共用 label 群/日期群/radio 群自畫。
  // ------------------------------------------------------------------
  const contractFields = legacyContractFieldsFor(pageId) ?? [];
  // formDone radio×3 要用**原始變體**（legacyContractFieldsFor 依 name 去重只剩 1 顆＝批次 41
  // 同根因）；legacy-fields.generated 的 raw 清單有三個變體（全部''/進行中 false/已完成 true）。
  const formDoneVariants = (LEGACY_FIELDS[pageId] ?? []).filter(
    (field) => field.name === 'formDone',
  );
  /** 「全部」radio 的送出語意值（checkedValueOf 凍結慣例＝'on'；applySearch 省略條件用）。 */
  const formDoneAllRadio = formDoneVariants.find((field) => (field.value ?? '') === '');
  const formDoneAllValue = formDoneAllRadio === undefined ? 'on' : checkedValueOf(formDoneAllRadio);
  const pick = (name: string): LegacyFieldControl | undefined =>
    contractFields.find((field) => field.name === name);
  const headFields = ['annual', 'organization', 'jobType', 'planTarget']
    .map(pick)
    .filter((field) => field !== undefined) as readonly LegacyFieldControl[];
  const enableTypeField = pick('enableType');

  const [draftFieldValues, setDraftFieldValues] = useState<Record<string, string>>(() => {
    const initial = { ...initialValues(contractFields, pageId) };
    // formDone 全部 radio＝legacy checked 預設（queryPage.jsp:170 checked）；C5-h 送出語意
    // =checkedValueOf（'on'，凍結慣例）；applySearch 以 formDoneAllValue 省略。
    const allRadio = formDoneVariants.find((field) => (field.value ?? '') === '');
    if (allRadio !== undefined) initial['formDone'] = checkedValueOf(allRadio);
    return initial;
  });
  const [onlyTodoForm, setOnlyTodoForm] = useState(false);
  const setFieldValue = (name: string, next: string) => {
    setDraftFieldValues((current) => ({ ...current, [name]: next }));
  };

  // ------------------------------------------------------------------
  // 清單（queryPage.js:156-263 queryForms；formDone 空＝不送、onlyTodoForm=true 才送）。
  // ------------------------------------------------------------------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [keyword, setKeyword] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { data, loading, error, reload } = useList(pageId, { page, pageSize, keyword, filters });
  const rows: readonly Row[] = data?.rows ?? [];

  function applySearch() {
    // queryPage.js:264-290 getQueryParams：formDone ''＝不送；onlyTodoForm checked 才送；
    // onlyTodoForm=true 時 legacy 跳過 form.valid()（日期 required 例外，:160）。
    const payload = toPayload(draftFieldValues, contractFields, pageId);
    const next: Record<string, string> = {};
    for (const [key, value] of Object.entries(payload)) {
      // queryPage.js:287 formDone 「全部」（空值語意）不送；'on'＝全部的送出語意值。
      if (key === 'formDone' && value === formDoneAllValue) continue;
      next[key] = value;
    }
    if (onlyTodoForm) next['onlyTodoForm'] = 'true';
    setFilters(next);
    setPage(1);
  }

  // ------------------------------------------------------------------
  // 選取（queryPage.js:120-124 select-all／:377-384 getSelectedForms page:'all'）。
  // ------------------------------------------------------------------
  const [checked, setChecked] = useState<Map<string, Row>>(new Map());
  const toggleRow = (row: Row) => {
    const id = String(row.id ?? '');
    setChecked((current) => {
      const next = new Map(current);
      if (next.has(id)) next.delete(id);
      else next.set(id, row);
      return next;
    });
  };
  const selectedIds = () => [...checked.keys()];
  const allSelected = rows.length > 0 && rows.every((row) => checked.has(String(row.id ?? '')));
  const toggleAll = () => {
    setChecked((current) => {
      const next = new Map(current);
      if (allSelected) {
        for (const row of rows) next.delete(String(row.id ?? ''));
      } else {
        for (const row of rows) next.set(String(row.id ?? ''), row);
      }
      return next;
    });
  };

  // ------------------------------------------------------------------
  // 批量（queryPage.js:110-148；CoEditFormUtils.js:103-143）。legacy 無空選驗證；
  // 刪除有 sysConfirm；啟用/停用/變更為未完成無 confirm 直打＋info(desc)＋重查。
  // ------------------------------------------------------------------
  async function bulkStatus(word: '啟用' | '停用'): Promise<void> {
    const ids = selectedIds();
    if (ids.length === 0) {
      // legacy 無空選驗證（POST 空陣列由 server 回應）；雛形對空選誠實提示。
      await dialogs.alert('請先勾選要操作的表單【雛形值待實機校準】');
      return;
    }
    try {
      await dialogs.processing.wrap(
        Promise.all(ids.map((id) => dataSource.update(pageId, id, { enabled: word === '啟用' ? '1' : '0' }))),
      );
      setChecked(new Map());
      reload();
      await dialogs.info(t('state.saveSuccess'));
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    }
  }
  async function bulkDelete(): Promise<void> {
    const ids = selectedIds();
    if (ids.length === 0) {
      await dialogs.alert('請先勾選要操作的表單【雛形值待實機校準】');
      return;
    }
    // queryPage.js:111 sysConfirm("請問是否要刪除?")。
    const confirmed = await dialogs.confirm('請問是否要刪除?');
    if (!confirmed) return;
    try {
      await dialogs.processing.wrap(Promise.all(ids.map((id) => dataSource.remove(pageId, id))));
      setChecked(new Map());
      reload();
      await dialogs.info(t('state.saveSuccess'));
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    }
  }
  async function bulkReopen(): Promise<void> {
    const ids = selectedIds();
    if (ids.length === 0) {
      await dialogs.alert('請先勾選要操作的表單【雛形值待實機校準】');
      return;
    }
    try {
      await dialogs.processing.wrap(Promise.all(ids.map((id) => dataSource.update(pageId, id, { done: '0' }))));
      setChecked(new Map());
      reload();
      await dialogs.info(t('state.saveSuccess'));
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    }
  }

  // ------------------------------------------------------------------
  // 表格（:221-232 formListTable；表頭 11 欄第一欄空＝select-all :227）。
  // ------------------------------------------------------------------
  const columns = columnsFor(pageId);
  const sequence = legacyTableSequenceFor(pageId);
  const checkColumnKey = sequence?.find((column) => column.actions)?.key ?? 'actions';
  const tableColumns: readonly TableColumn[] =
    sequence === undefined
      ? columns.map((column) => ({ key: column.key, header: column.label }))
      : sequence.map((column) => ({
          key: column.key,
          header:
            column.key === checkColumnKey ? (
              <input
                id="select-all"
                type="checkbox"
                data-ui-id={`${pageId}-select-all`}
                checked={allSelected}
                onChange={toggleAll}
                className="size-4 accent-brand"
              />
            ) : (
              column.header
            ),
        }));

  const legacyEmptyMessage = resolveLegacyMessage(pageId, 'empty');
  async function exportRows(): Promise<void> {
    try {
      await dataSource.list(pageId, { page, pageSize, keyword, filters, export: true });
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    }
  }

  const resultShell = (
    <DataTableShell
      uiId={`${pageId}-result`}
      page={page}
      pageSize={pageSize}
      total={data?.total ?? 0}
      keyword={keyword}
      loading={loading}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
      onKeywordChange={(next) => {
        setKeyword(next);
        setPage(1);
        reload();
      }}
      {...(legacyEmptyMessage === null ? {} : { emptyText: legacyEmptyMessage.text })}
      exportEffect={`list:${pageId}#export`}
      onExport={() => {
        void exportRows();
      }}
    >
      <Table
        uiId={`${pageId}-result-table`}
        columns={tableColumns}
        rows={rows}
        rowKey={rowUiKey}
        emptyText=""
        renderCell={(row, columnKey) => {
          if (columnKey === checkColumnKey) {
            // formCheckBox 勾選欄（queryPage.js:122-123；getSelectedForms :380 name=formCheckBox）。
            const id = String(row.id ?? '');
            return (
              <input
                type="checkbox"
                data-ui-id={`${pageId}-row-${toUiKey(id)}-check-form`}
                checked={checked.has(id)}
                onChange={() => {
                  toggleRow(row);
                }}
                className="size-4 accent-brand"
              />
            );
          }
          if (columnKey === 'title') {
            // 批次 46（FRM-002，Gitea issue #44）：表單名稱＝openForm 連結（兩行）。
            // queryPage.js:203-213 data 'form.formTemplateName'：a.openForm 帶 formKey
            //（:212），formTargetTypeName 非空時以 <br> 串接第二行（:208-211）—— React 以
            // block span 呈現對齊（僅非空時加，照 legacy 條件）。點擊 queryPage.js:104-108
            // a.openForm → CoEditFormUtils.js:48-50 openFillFormPage：
            //   window.open(`coEditForm/prompt/form?formKey=${formKey}`, '_blank')
            // 站內導航鏡像（D19-2／批次 45 慣例，不真的開新視窗）。legacy 裸字串串接；
            // React 加 encodeURIComponent（種子 ASCII 零差異，特殊字元衛生）。
            const formKey = String(row['formKey'] ?? '');
            if (formKey === '') {
              // 種子未帶 formKey 的列＝維持純文字，不 invent 導流行為。
              const raw = row[columnKey];
              return raw === undefined || raw === null ? '' : String(raw);
            }
            const targetTypeName = String(row['formTargetTypeName'] ?? '');
            return (
              <button
                type="button"
                className="cursor-pointer text-[#1ab394] underline"
                data-ui-id={`${pageId}-row-${toUiKey(String(row.id ?? ''))}-open-form`}
                onClick={() => {
                  navigate(`/coEditForm/prompt/form?formKey=${encodeURIComponent(formKey)}`);
                }}
              >
                <span className="block">{String(row[columnKey] ?? '')}</span>
                {targetTypeName === '' ? null : <span className="block">{targetTypeName}</span>}
              </button>
            );
          }
          const raw = row[columnKey];
          return raw === undefined || raw === null ? '' : String(raw);
        }}
      />
    </DataTableShell>
  );

  // ------------------------------------------------------------------
  // 整頁（legacy 順序：查詢 form＋右欄說明 → 表格）。
  // ------------------------------------------------------------------
  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={routes.find((entry) => entry.pageId === pageId)?.title ?? pageId}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {error !== null ? (
          <TemplateError uiId={`${pageId}-error`} error={error} onRetry={reload} pageId={pageId} />
        ) : null}
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-7">
            {/* form#searchForm（:34-209）；Enter 送出＝原生 submit（queryPage.js:156 queryForms）。 */}
            <form
              data-ui-id={`${pageId}-search`}
              noValidate
              className="flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                applySearch();
                reload();
              }}
            >
              <LegacyContractFields
                pageId={pageId}
                fields={headFields}
                values={draftFieldValues}
                onChange={setFieldValue}
                disabled={loading}
              />
              {/* 表單名稱（:102-113）：一個 label 對 formDepartment＋formTemplate(multiple)。 */}
              <div data-ui-id={`${pageId}-search-form-name-group`}>
                <label className="mb-[5px] inline-block font-bold text-ink">表單名稱：</label>
                <LegacyContractFields
                  pageId={pageId}
                  fields={['formDepartment', 'formTemplate'].map(pick).filter((f) => f !== undefined) as readonly LegacyFieldControl[]}
                  values={draftFieldValues}
                  onChange={setFieldValue}
                  disabled={loading}
                  hideLabels
                />
              </div>
              {/* 參與表單填寫員（:115-125）：一個 label 對 keyword＋account。 */}
              <div data-ui-id={`${pageId}-search-filler-group`}>
                <label className="mb-[5px] inline-block font-bold text-ink">參與表單填寫員：</label>
                <LegacyContractFields
                  pageId={pageId}
                  fields={['keyword', 'account'].map(pick).filter((f) => f !== undefined) as readonly LegacyFieldControl[]}
                  values={draftFieldValues}
                  onChange={setFieldValue}
                  disabled={loading}
                  hideLabels
                />
              </div>
              {/* 課程日期（:127-154）：* 課程日期：label＋起訖（readonly required maxlength=10）
                  ＋reset 圓鈕（:151-152 fa-eraser icon-only＝空文字按鈕，批次 41 前例）。 */}
              <div data-ui-id={`${pageId}-search-dates-group`}>
                <label className="mb-[5px] inline-block font-bold text-ink">* 課程日期：</label>
                <LegacyContractFields
                  pageId={pageId}
                  fields={['classDateStart', 'classDateEnd'].map(pick).filter((f) => f !== undefined) as readonly LegacyFieldControl[]}
                  values={draftFieldValues}
                  onChange={setFieldValue}
                  disabled={loading}
                  hideLabels
                />
                <button
                  type="button"
                  data-ui-id={`${pageId}-query-dates-reset`}
                  aria-label="清除課程日期"
                  className="inline-block size-8 rounded-full border border-border text-ink hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-[.65]"
                  disabled={loading}
                  onClick={() => {
                    setFieldValue('classDateStart', '');
                    setFieldValue('classDateEnd', '');
                  }}
                />
              </div>
              {/* 啟用狀態（:155-162）：契約 1:1 欄位照畫（label 由 LegacyContractFields 帶）。 */}
              {enableTypeField !== undefined ? (
                <LegacyContractFields
                  pageId={pageId}
                  fields={[enableTypeField]}
                  values={draftFieldValues}
                  onChange={setFieldValue}
                  disabled={loading}
                />
              ) : null}
              {/* 表單進行狀態 radio 群（:165-176）；queryPage.js:287 formDone=''不送。 */}
              <div data-ui-id={`${pageId}-search-form-done-group`}>
                <label className="mb-[5px] inline-block font-bold text-ink">表單進行狀態：</label>
                <LegacyContractFields
                  pageId={pageId}
                  fields={formDoneVariants}
                  values={draftFieldValues}
                  onChange={setFieldValue}
                  disabled={loading}
                />
              </div>
              {/* 自己待填的表單（:179-183）；queryPage.js:269 onlyTodoForm=true 才送。 */}
              <div data-ui-id={`${pageId}-search-only-todo-group`}>
                <label className="mb-[5px] inline-block font-bold text-ink">自己待填的表單：</label>
                <input
                  id="onlyTodoForm"
                  type="checkbox"
                  data-ui-id={`${pageId}-search-only-todo`}
                  checked={onlyTodoForm}
                  onChange={(event) => {
                    setOnlyTodoForm(event.target.checked);
                  }}
                  className="size-4 accent-brand"
                />
                <label htmlFor="onlyTodoForm" className="text-sm text-ink">
                  (只顯示自己待填的表單)
                </label>
              </div>
              {/* 按鈕列（:186-207）：查詢/啟用/停用/刪除/變更為未完成（查詢=submit）。
                  啟用/停用/變更為未完成端點未映射＝不帶 dataEffect（D21-4 誠實登記）。 */}
              <div data-ui-id={`${pageId}-actions`} className="flex flex-wrap items-center gap-2">
                <Button
                  uiId={`${pageId}-search-submit`}
                  variant="primary"
                  size="sm"
                  type="button"
                  disabled={loading}
                  dataEffect={`list:${pageId}`}
                  onClick={() => {
                    applySearch();
                    reload();
                  }}
                >
                  查詢
                </Button>
                <Button
                  uiId={`${pageId}-btn-enable`}
                  variant="default"
                  size="sm"
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    void bulkStatus('啟用');
                  }}
                >
                  啟用
                </Button>
                <Button
                  uiId={`${pageId}-btn-disable`}
                  variant="default"
                  size="sm"
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    void bulkStatus('停用');
                  }}
                >
                  停用
                </Button>
                <Button
                  uiId={`${pageId}-btn-delete`}
                  variant="danger"
                  size="sm"
                  type="button"
                  disabled={loading}
                  dataEffect={`remove:${pageId}`}
                  onClick={() => {
                    void bulkDelete();
                  }}
                >
                  刪除
                </Button>
                <Button
                  uiId={`${pageId}-btn-reopen`}
                  variant="default"
                  size="sm"
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    void bulkReopen();
                  }}
                >
                  變更為未完成
                </Button>
              </div>
            </form>
          </div>
          {/* 右欄說明（:211-218）＝泛用說明區（裁決③；legacy-aside-notes 地圖）。 */}
          <div className="md:col-span-5">
            <LegacyAsideBlocks pageId={pageId} />
          </div>
        </div>
        {resultShell}
      </ContentPanel>
    </div>
  );
}
